import React, { useEffect, useMemo, useState } from "react";
import Icons from "../icons";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* Wizard steps, in order. */
const STEPS = [
  { id: "details", label: "Details", hint: "Name your report" },
  { id: "execution", label: "Execution", hint: "Pick an execution" },
  { id: "types", label: "Report types", hint: "Choose what to include" },
  { id: "review", label: "Review", hint: "Preview & create" },
];

/* Selectable report types (mirrors backend REPORT_TYPES) with descriptions. */
const REPORT_TYPES = [
  { id: "test_summary", label: "Test Summary", hint: "Scope, metrics, environment, exit criteria & sign-off" },
  { id: "detailed", label: "Detailed Test", hint: "Every step with before/after evidence screenshots" },
  { id: "defect", label: "Defect", hint: "Failed cases as defects — severity, module, status" },
  { id: "coverage", label: "Test Coverage", hint: "Requirement / ticket ↔ test-case mapping & gaps" },
  { id: "automation", label: "Automation", hint: "Automated run results: pass/fail, steps, timestamps" },
  { id: "progress", label: "Progress / Status", hint: "Execution status, counts and what's left" },
];

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

/* ------------------------------------------------------------------ */
/*  Stepper header                                                     */
/* ------------------------------------------------------------------ */
function Stepper({ current, onStepClick, maxReached }) {
  return (
    <ol className="flex items-center">
      {STEPS.map((step, idx) => {
        const isDone = idx < current;
        const isActive = idx === current;
        const clickable = idx <= maxReached;
        return (
          <li key={step.id} className="flex flex-1 items-center last:flex-none">
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick(idx)}
              className={[
                "flex items-center gap-3 text-left",
                clickable ? "cursor-pointer" : "cursor-not-allowed",
              ].join(" ")}
            >
              <span
                className={[
                  "flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-semibold ring-1 transition",
                  isActive
                    ? "bg-brand-600 text-white ring-brand-600"
                    : isDone
                    ? "bg-brand-50 text-brand-700 ring-brand-600/30"
                    : "bg-white text-slate-400 ring-slate-300",
                ].join(" ")}
              >
                {isDone ? <Icons.Check className="h-5 w-5" /> : idx + 1}
              </span>
              <span className="hidden sm:block">
                <span
                  className={[
                    "block text-sm font-semibold",
                    isActive || isDone ? "text-slate-900" : "text-slate-400",
                  ].join(" ")}
                >
                  {step.label}
                </span>
                <span className="block text-xs text-slate-400">{step.hint}</span>
              </span>
            </button>
            {idx < STEPS.length - 1 && (
              <span
                className={[
                  "mx-3 h-px flex-1",
                  idx < current ? "bg-brand-500" : "bg-slate-200",
                ].join(" ")}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function resultChip(status) {
  switch (status) {
    case "completed":
      return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
    case "in_progress":
      return "bg-blue-50 text-blue-700 ring-blue-600/20";
    default:
      return "bg-slate-100 text-slate-600 ring-slate-500/20";
  }
}

/* ================================================================== */
/*  Wizard                                                             */
/* ================================================================== */
export default function CreateReportWizard({ userkey, onCancel, onCreated }) {
  const [step, setStep] = useState(0);
  const [maxReached, setMaxReached] = useState(0);

  // Step 1 — details
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [environment, setEnvironment] = useState("");
  const [buildLabel, setBuildLabel] = useState("");
  const [executor, setExecutor] = useState("");

  // Step 2 — execution
  const [executions, setExecutions] = useState([]);
  const [execLoading, setExecLoading] = useState(true);
  const [execError, setExecError] = useState("");
  const [executionkey, setExecutionkey] = useState(null);

  // Step 3 — report types (multi-select)
  const [types, setTypes] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Review — live PDF preview.
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const selectedExecution = executions.find((e) => e.executionkey === executionkey) || null;

  const goTo = (idx) => {
    setStep(idx);
    setMaxReached((m) => Math.max(m, idx));
  };
  const next = () => goTo(Math.min(step + 1, STEPS.length - 1));
  const back = () => goTo(Math.max(step - 1, 0));

  // Load executions once.
  useEffect(() => {
    let cancelled = false;
    setExecLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}executions`);
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
        setExecutions(Array.isArray(body?.data) ? body.data : []);
      } catch (e) {
        if (!cancelled) setExecError(e.message || "Failed to load executions.");
      } finally {
        if (!cancelled) setExecLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleType = (id) =>
    setTypes((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const canLeaveDetails = name.trim().length > 0;
  const canLeaveExecution = Boolean(executionkey);
  const canLeaveTypes = types.length > 0;

  const previewBody = useMemo(
    () => ({
      name: name.trim() || "Untitled report",
      description: description.trim(),
      environment: environment.trim() || null,
      build_label: buildLabel.trim() || null,
      executor: executor.trim() || null,
      executionkey,
      report_types: types,
    }),
    [name, description, environment, buildLabel, executor, executionkey, types]
  );

  // Live PDF preview on the review step.
  useEffect(() => {
    if (step !== 3 || !executionkey || types.length === 0) return;
    let cancelled = false;
    let objectUrl = "";
    setPreviewLoading(true);
    setPreviewError("");
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}reports/preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(previewBody),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      } catch {
        if (!cancelled) setPreviewError("Could not render the PDF preview.");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [step, previewBody, executionkey, types.length]);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${API_BASE_URL}reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          environment: environment.trim() || null,
          build_label: buildLabel.trim() || null,
          executor: executor.trim() || null,
          executionkey,
          execution_name: selectedExecution?.name || null,
          report_types: types,
          userkey: userkey || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.data?.reportkey) {
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      onCreated?.(body.data);
    } catch (e) {
      setSubmitError(e.message || "Could not create report.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          <Icons.ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Create report</h1>
          <p className="text-sm text-slate-500">
            Pick an execution and choose the report types to generate.
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-card p-5 shadow-sm">
        <Stepper current={step} onStepClick={goTo} maxReached={maxReached} />
      </div>

      {/* Step body */}
      <div className="rounded-xl border border-slate-200 bg-card p-6 shadow-sm">
        {/* ---- Step 1: Details ---- */}
        {step === 0 && (
          <div className="max-w-xl space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Report name</label>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Release 1.2 — Test Report"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Description <span className="text-slate-400">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="What does this report cover?"
                className={inputCls}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Environment <span className="text-slate-400">(optional)</span>
                </label>
                <input value={environment} onChange={(e) => setEnvironment(e.target.value)} placeholder="e.g. QA, Staging" className={inputCls} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Build <span className="text-slate-400">(optional)</span>
                </label>
                <input value={buildLabel} onChange={(e) => setBuildLabel(e.target.value)} placeholder="e.g. 2.4.1" className={inputCls} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Executor <span className="text-slate-400">(optional)</span>
                </label>
                <input value={executor} onChange={(e) => setExecutor(e.target.value)} placeholder="Who ran the tests" className={inputCls} />
              </div>
            </div>
          </div>
        )}

        {/* ---- Step 2: Execution ---- */}
        {step === 1 && (
          <div>
            <p className="mb-4 text-sm text-slate-500">Select the execution to draw this report from.</p>
            {execLoading ? (
              <div className="flex items-center gap-2 py-10 text-slate-400">
                <Icons.Spinner className="h-5 w-5 animate-spin" /> Loading executions…
              </div>
            ) : execError ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{execError}</div>
            ) : executions.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 px-6 py-12 text-center text-sm text-slate-500">
                No executions yet. Create one on the Executions page first.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {executions.map((ex) => {
                  const active = executionkey === ex.executionkey;
                  return (
                    <button
                      key={ex.executionkey}
                      type="button"
                      onClick={() => setExecutionkey(ex.executionkey)}
                      className={[
                        "flex flex-col rounded-xl border p-5 text-left shadow-sm transition",
                        active ? "border-brand-500 ring-2 ring-brand-500/30" : "border-slate-200 hover:border-brand-300 hover:shadow-md",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="min-w-0 truncate font-semibold text-slate-900">{ex.name}</span>
                        {active && (
                          <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand-600 text-white">
                            <Icons.Check className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                      <span className={["mt-2 inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset", resultChip(ex.status)].join(" ")}>
                        {ex.status}
                      </span>
                      <div className="mt-3 flex items-center gap-1.5 text-[11px] font-medium">
                        <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-emerald-700 ring-1 ring-inset ring-emerald-600/20">{ex.passed} pass</span>
                        <span className="rounded-full bg-rose-50 px-1.5 py-0.5 text-rose-700 ring-1 ring-inset ring-rose-600/20">{ex.failed} fail</span>
                        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-500">{ex.total} total</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ---- Step 3: Report types ---- */}
        {step === 2 && (
          <div>
            <p className="mb-4 text-sm text-slate-500">
              Choose one or more report types. Each adds its own sections to the generated report.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {REPORT_TYPES.map((t) => {
                const active = types.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleType(t.id)}
                    className={[
                      "flex items-start gap-3 rounded-xl border p-4 text-left transition",
                      active ? "border-brand-500 bg-brand-50/60 ring-1 ring-brand-500/30" : "border-slate-200 hover:border-brand-300",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded border",
                        active ? "border-brand-600 bg-brand-600 text-white" : "border-slate-300 bg-white",
                      ].join(" ")}
                    >
                      {active && <Icons.Check className="h-3.5 w-3.5" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900">{t.label}</span>
                      <span className="block text-xs text-slate-500">{t.hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ---- Step 4: Review ---- */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Report name</p>
                <p className="mt-1 text-sm font-medium text-slate-800">{name}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Execution</p>
                <p className="mt-1 text-sm font-medium text-slate-800">{selectedExecution?.name || "—"}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Report types</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {types.map((id) => (
                    <span key={id} className="rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700 ring-1 ring-inset ring-brand-600/20">
                      {REPORT_TYPES.find((t) => t.id === id)?.label || id}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Report preview</p>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                {previewLoading ? (
                  <div className="flex items-center justify-center gap-2 py-24 text-slate-400">
                    <Icons.Spinner className="h-5 w-5 animate-spin" /> Rendering PDF preview…
                  </div>
                ) : previewError ? (
                  <div className="px-4 py-16 text-center text-sm text-rose-600">{previewError}</div>
                ) : previewUrl ? (
                  <iframe title="Report PDF preview" src={`${previewUrl}#toolbar=0&navpanes=0`} className="h-[600px] w-full" />
                ) : (
                  <div className="px-4 py-16 text-center text-sm text-slate-400">Pick an execution and at least one report type to preview.</div>
                )}
              </div>
            </div>

            {submitError && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{submitError}</div>
            )}
          </div>
        )}
      </div>

      {/* Footer nav */}
      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={step === 0 ? onCancel : back}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          {step === 0 ? "Cancel" : (<><Icons.ChevronLeft className="h-4 w-4" /> Back</>)}
        </button>

        {step < STEPS.length - 1 ? (
          <button
            type="button"
            onClick={next}
            disabled={
              (step === 0 && !canLeaveDetails) ||
              (step === 1 && !canLeaveExecution) ||
              (step === 2 && !canLeaveTypes)
            }
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue
            <Icons.ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !executionkey || types.length === 0 || !name.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? (<><Icons.Spinner className="h-4 w-4 animate-spin" /> Creating…</>) : (<><Icons.Check className="h-4 w-4" /> Create report</>)}
          </button>
        )}
      </div>
    </div>
  );
}
